/** Small shared pieces for the calendar screens: kind icons, RSVP tones, audience labels, links. */
import { CalendarDays, ChevronRight, Music, Star, UsersRound, type LucideIcon } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { groupsFromMask, matchesOwnAudience, roleAtLeast, type AudienceGroup, type Section } from '../../../shared/audience';
import type { EventDTO, EventKind, Rsvp } from '../../../shared/dto/events';
import type { ErrorCode } from '../../../shared/errors';
import { ApiFailure, isNetworkFailure } from '../../api/client';
import { useMe } from '../../auth/AuthProvider';
import { cx } from '../../components/ui';
import type { MessageKey, T } from '../../i18n';

export const EVENT_KINDS: readonly EventKind[] = ['practice', 'sectional', 'concert', 'other'];

export const KIND_ICON: Record<EventKind, LucideIcon> = {
  practice: Music,
  sectional: UsersRound,
  concert: Star,
  other: CalendarDays,
};

export function kindKey(kind: EventKind): MessageKey {
  return `calendar.kind.${kind}`;
}

export function KindIcon({ kind, className }: { kind: EventKind; className?: string }) {
  const Icon = KIND_ICON[kind];
  return <Icon className={cx('shrink-0', className ?? 'size-5')} aria-hidden />;
}

/** Round icon badge used in cards/rows. */
export function KindBadge({ kind, cancelled, size = 'md' }: { kind: EventKind; cancelled?: boolean; size?: 'md' | 'lg' }) {
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-2xl',
        size === 'lg' ? 'size-14' : 'size-11',
        cancelled ? 'bg-[var(--surface-2)] text-[var(--muted)]' : 'bg-[var(--accent-soft)] text-[var(--accent)]',
      )}
    >
      <KindIcon kind={kind} className={size === 'lg' ? 'size-7' : 'size-6'} />
    </span>
  );
}

export const RSVP_KEY: Record<Rsvp, MessageKey> = {
  yes: 'calendar.rsvp.yes',
  no: 'calendar.rsvp.no',
  maybe: 'calendar.rsvp.maybe',
};

/** Visual state of an event for me: my effective RSVP, cancelled, or not aimed at me. */
export type Tone = Rsvp | 'none' | 'cancelled' | 'other';

export function eventTone(ev: EventDTO, section: Section | null): Tone {
  if (ev.status === 'cancelled') return 'cancelled';
  if (!matchesOwnAudience(ev.audienceMask, section)) return 'other';
  return ev.my.effectiveRsvp ?? 'none';
}

/** Small status dot (month grid / legend). */
export function ToneDot({ tone, className }: { tone: Tone; className?: string }) {
  const base = 'relative inline-block size-2.5 shrink-0 rounded-full';
  switch (tone) {
    case 'yes':
      return <span aria-hidden className={cx(base, 'bg-[var(--ok)]', className)} />;
    case 'no':
      return <span aria-hidden className={cx(base, 'bg-[var(--muted)] opacity-70', className)} />;
    case 'maybe':
      return <span aria-hidden className={cx(base, 'bg-[var(--warn)]', className)} />;
    case 'none':
      return <span aria-hidden className={cx(base, 'border-2 border-[var(--accent)]', className)} />;
    case 'other':
      return <span aria-hidden className={cx(base, 'border-2 border-[var(--muted)] opacity-60', className)} />;
    case 'cancelled':
      return (
        <span aria-hidden className={cx(base, 'border-2 border-[var(--muted)] opacity-80', className)}>
          <span className="absolute top-1/2 left-1/2 h-0.5 w-3.5 -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-[var(--muted)]" />
        </span>
      );
  }
}

/**
 * Solid fills for white text. --ok / --warn are darkened locally so white text meets WCAG AA
 * (≈5.3:1 on green, ≈5.9:1 on amber; plain --ok / --warn give only ≈4.2:1 / ≈3.6:1).
 */
export const OK_FILL = 'bg-[color-mix(in_srgb,var(--ok)_85%,black)]';
export const WARN_FILL = 'bg-[color-mix(in_srgb,var(--warn)_75%,black)]';

/** Colours for pills / chips per tone (CSS variables keep light/dark working). */
export const TONE_PILL: Record<Tone, string> = {
  yes: 'bg-[color-mix(in_srgb,var(--ok)_16%,transparent)] text-[var(--ink)] border-l-4 border-[var(--ok)]',
  no: 'bg-[var(--surface-2)] text-[var(--muted)] border-l-4 border-[var(--muted)]',
  maybe: 'bg-[color-mix(in_srgb,var(--warn)_16%,transparent)] text-[var(--ink)] border-l-4 border-[var(--warn)]',
  none: 'bg-[var(--accent-soft)] text-[var(--ink)] border-l-4 border-[var(--accent)]',
  other: 'bg-[var(--surface-2)] text-[var(--ink-2)] border-l-4 border-[var(--line)]',
  cancelled: 'bg-[var(--surface-2)] text-[var(--muted)] line-through border-l-4 border-[var(--line)]',
};

export function useIsEditor(): boolean {
  const me = useMe();
  return !!me && roleAtLeast(me.role, 'editor');
}

/** Can the signed-in user RSVP / check in to this event (it targets their section or everyone)? */
export function canParticipate(ev: EventDTO, section: Section | null): boolean {
  return matchesOwnAudience(ev.audienceMask, section);
}

export function audienceLabelKey(g: AudienceGroup): MessageKey {
  if (g === 'all') return 'audience.all';
  if (g === 'musician') return 'section.musicians';
  return `section.${g}`;
}

export function audienceText(mask: number, t: T): string {
  return groupsFromMask(mask)
    .map((g) => t(audienceLabelKey(g)))
    .join(', ');
}

/**
 * Calendar-specific server reasons (ApiFailure message or details) that retrying never fixes, so
 * they get their own explanation instead of the generic "Árekstur – reyndu aftur".
 */
const REASON_KEY: ReadonlyMap<string, MessageKey> = new Map<string, MessageKey>([
  ['started', 'calendar.err.started'],
  ['cancelled', 'calendar.err.cancelled'],
  ['series_ended', 'calendar.err.series_ended'],
  ['outside_window', 'calendar.err.outside_window'],
  ['bad_time_range', 'calendar.err.bad_time_range'],
  ['setlist_not_found', 'calendar.err.setlist_not_found'],
  ['program_song_not_found', 'calendar.err.program_song_not_found'],
  ['program_collection_not_found', 'calendar.err.program_collection_not_found'],
  ['reply_to_invalid', 'calendar.err.reply_to_invalid'],
  ['event deleted', 'calendar.err.deleted'],
  ['series deleted', 'calendar.err.deleted'],
]);

function reasonKey(e: ApiFailure): MessageKey | null {
  return (typeof e.details === 'string' ? REASON_KEY.get(e.details) : undefined) ?? REASON_KEY.get(e.message) ?? null;
}

/** Human message for a thrown API error. */
export function errorText(t: T, e: unknown): string {
  if (isNetworkFailure(e)) return t('error.network');
  if (e instanceof ApiFailure) {
    const reason = reasonKey(e);
    if (reason) return t(reason);
    const key = `error.${e.code satisfies ErrorCode}` as const;
    return t(key);
  }
  return t('common.error');
}

/** Map search link for a free-text location (Apple Maps opens the Maps app on iPad/iPhone). */
export function mapsUrl(location: string): string {
  return `https://maps.apple.com/?q=${encodeURIComponent(location)}`;
}

const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

/** Plain text with http(s) links made clickable — React elements only, never raw HTML. */
export function Linkified({ text }: { text: string }) {
  const parts = text.split(URL_RE);
  const out: ReactNode[] = [];
  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      // Keep trailing punctuation outside the link.
      const m = /^(.*?)([).,!?;:]*)$/.exec(part);
      const url = m?.[1] ?? part;
      const tail = m?.[2] ?? '';
      out.push(
        <Fragment key={i}>
          <a href={url} target="_blank" rel="noopener noreferrer" className="break-all text-[var(--accent)] underline underline-offset-2">
            {url}
          </a>
          {tail}
        </Fragment>,
      );
    } else if (part) out.push(<Fragment key={i}>{part}</Fragment>);
  });
  return <>{out}</>;
}

/** Sort order for sections in headcount / roster lists. */
export const SECTION_ORDER: readonly (Section | null)[] = ['T1', 'T2', 'B1', 'B2', 'conductor', 'musician', null];

export function sectionGroupKey(s: Section | null): MessageKey {
  if (s === null) return 'section.none';
  if (s === 'musician') return 'section.musicians';
  return `section.${s}`;
}

/** Large tappable choice (icon, title, description) used in "what do you want to do" sheets. */
export function ChoiceButton({ icon: Icon, title, desc, onClick }: { icon: LucideIcon; title: string; desc?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-20 w-full items-center gap-4 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4 text-left transition active:bg-[var(--accent-soft)]"
    >
      <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)]">
        <Icon className="size-6" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[18px] font-semibold">{title}</span>
        {desc ? <span className="block text-[15px] text-[var(--muted)]">{desc}</span> : null}
      </span>
      <ChevronRight className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
    </button>
  );
}
